/*
 * Step3PreviewImpact - Delta analysis, CSV migration preview, and version creation
 *
 * UI/UX DESCRIPTION:
 * - Enhanced DeltaWarningDialog showing CSV migration preview
 * - Allocation impact analysis
 * - Manual payment impact analysis
 * - Visual indicators for data preservation/loss
 * - Create version button with loading state
 *
 * FUNCTIONALITY:
 * - Run delta analysis on new vs old rules
 * - Show which CSVs can be migrated vs incompatible
 * - Preview allocation and manual payment impacts
 * - Create new draft version on confirmation
 */

import React, { useState, useEffect } from 'react';
import {
  WarningIcon,
  CheckCircleIcon,
  XCircleIcon,
  DownloadIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import { Card, CardContent, CardHeader } from 'src/components/ui/card';
import { Badge } from 'src/components/ui/badge';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { DeltaWarningDialog } from 'src/components/rebates/DeltaWarningDialog';
import {
  useAnalyzeVersionPreview,
  ExtractRulesOnlyResponse,
} from 'src/api/rebates/useCreateVersionWithExtraction';
import ApiResponse from 'src/api/ApiResponse';
import { DeltaAnalysisResult } from '@erp/shared-types';
import { RuleWithVersionState } from './types';
import { cn } from '@/lib/utils';
// Types
interface RebateAgreement {
  id: string;
  original_agreement_id: string;
  name: string;
  status: string;
}

interface RebateVersion {
  id: string;
  versionNumber: number;
  status: string;
}

interface Step3PreviewImpactProps {
  agreement: RebateAgreement;
  latestVersion: RebateVersion;
  allRules: RuleWithVersionState[]; // Unified rule list with inclusion state
  productGroups: ExtractRulesOnlyResponse['productGroups'];
  onComplete: (deltaAnalysis: DeltaAnalysisResult) => void;
  onError: (error: string) => void;
  onBack: () => void;
  isLoading: boolean;
  error?: string;
  invoices?: Array<{
    id: string;
    files?: {
      transformedFile?: { id: string; name: string } | null;
    };
  }>;
  onDownloadDocument?: (documentId: string, fileName?: string) => void;
  onCreateVersion: (deltaAnalysis: DeltaAnalysisResult) => void;
  isCreating: boolean;
}

const Step3PreviewImpact: React.FC<Step3PreviewImpactProps> = ({
  agreement,
  latestVersion,
  allRules,
  productGroups,
  onComplete,
  onError,
  onBack,
  isLoading,
  error,
  invoices,
  onDownloadDocument,
  onCreateVersion,
  isCreating,
}) => {
  const [deltaAnalysis, setDeltaAnalysis] =
    useState<DeltaAnalysisResult | null>(null);
  const [showDeltaDialog, setShowDeltaDialog] = useState(false);

  const analyzePreviewMutation = useAnalyzeVersionPreview(
    latestVersion?.id || '',
  );

  useEffect(() => {
    // Run analysis when component mounts
    if (latestVersion?.id && allRules.length > 0) {
      // Filter rules to include in new version and separate by source
      const includedRules = allRules.filter((r) => r.includeInNewVersion);

      // API now accepts RebateRule format (camelCase) directly
      const extractedRules = includedRules
        .filter((r) => r.source === 'extracted')
        .map((r) => r.rule);

      const restoredRuleIds = includedRules
        .filter((r) => r.source === 'previous')
        .map((r) => r.rule.id);

      analyzePreviewMutation.mutate(
        {
          extractedRules,
          restoredRuleIds,
        },
        {
          onSuccess: (data: ApiResponse<DeltaAnalysisResult>) => {
            setDeltaAnalysis(data.data);
          },
          onError: (error: Error) => {
            const errorMessage =
              error.message || 'Failed to analyze version impact';
            console.error('Failed to analyze version impact:', error);
            onError(errorMessage);
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestVersion?.id, allRules]);

  const handleProceed = async () => {
    if (!deltaAnalysis) {
      return;
    }

    // Trigger version creation in parent
    onCreateVersion(deltaAnalysis);
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'none':
        return 'text-green-600';
      case 'minor':
        return 'text-blue-600';
      case 'major':
        return 'text-orange-600';
      case 'destructive':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'none':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'minor':
        return <CheckCircleIcon className="h-5 w-5 text-blue-600" />;
      case 'major':
        return <WarningIcon className="h-5 w-5 text-orange-600" />;
      case 'destructive':
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      default:
        return <WarningIcon className="h-5 w-5 text-gray-600" />;
    }
  };
  const hasPreservedAllocations =
    deltaAnalysis?.paymentImpact?.preservedAllocations &&
    deltaAnalysis?.paymentImpact?.preservedAllocations > 0;
  const hasRemovedAllocations =
    deltaAnalysis?.paymentImpact?.affectedAllocations &&
    deltaAnalysis?.paymentImpact?.affectedAllocations > 0;

  const preservedManualPaymentsCount = Math.max(
    0,
    (deltaAnalysis?.manualAmountImpact?.totalManualAmounts || 0) -
      (deltaAnalysis?.manualAmountImpact?.affectedManualAmounts || 0),
  );
  const hasPreservedManualPayments = preservedManualPaymentsCount > 0;
  const hasRemovedManualPayments =
    deltaAnalysis?.manualAmountImpact?.affectedManualAmounts &&
    deltaAnalysis?.manualAmountImpact?.affectedManualAmounts > 0;

  const hasCompatibleInvoices =
    deltaAnalysis?.invoiceImpact?.compatibleInvoices &&
    deltaAnalysis?.invoiceImpact?.compatibleInvoices > 0;
  const hasIncompatibleInvoices =
    deltaAnalysis?.invoiceImpact?.incompatibleInvoices &&
    deltaAnalysis?.invoiceImpact?.incompatibleInvoices > 0;

  const hasPreservedStyle = 'bg-green-100';
  const hasRemovedStyle = 'bg-red-100';
  const noPreservedStyle = 'bg-gray-100';
  const noRemovedStyle = 'bg-gray-100';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold mb-2">Preview Impact Analysis</h2>
        <p className="text-muted-foreground">
          Review how the rule changes will affect your existing data, including
          CSV migrations, payment allocations, and manually recorded rebates.
        </p>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-2 pb-4 border-b">
        <Button
          onClick={onBack}
          variant="outline"
          disabled={isLoading || analyzePreviewMutation.isPending}
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleProceed}
          disabled={
            isLoading ||
            analyzePreviewMutation.isPending ||
            !deltaAnalysis ||
            isCreating
          }
          className="gap-2"
        >
          {isCreating ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
              Creating Version...
            </>
          ) : (
            <>
              Create Version
              <CheckCircleIcon className="h-4 w-4 ml-2" />
            </>
          )}
        </Button>
      </div>

      {/* Error Display */}
      {error && (
        <AlertContainer variant="destructive">
          <WarningIcon className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </AlertContainer>
      )}

      {/* Analysis Status */}
      {analyzePreviewMutation.isPending && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
              <div>
                <p className="font-medium">Analyzing Impact</p>
                <p className="text-sm text-muted-foreground">
                  Calculating invoice compatibility and data impact...
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Analysis Results */}
      {deltaAnalysis && (
        <div className="space-y-4">
          {/* Overall Severity */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                {getSeverityIcon(deltaAnalysis.severity || 'none')}
                <div>
                  <h3 className="text-lg font-semibold">Impact Severity</h3>
                  <p
                    className={`text-sm font-medium ${getSeverityColor(deltaAnalysis.severity || 'none')}`}
                  >
                    {deltaAnalysis.severity
                      ? deltaAnalysis.severity.charAt(0).toUpperCase() +
                        deltaAnalysis.severity.slice(1)
                      : 'None'}{' '}
                    Impact
                  </p>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Invoice Impact */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                {hasIncompatibleInvoices ? (
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                )}
                <h3 className="text-lg font-semibold">
                  Vendor Reported Amounts Impact
                </h3>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={cn(
                    'text-center p-4 rounded-lg transition-colors',
                    hasCompatibleInvoices
                      ? hasPreservedStyle
                      : noPreservedStyle,
                  )}
                >
                  <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">
                    {deltaAnalysis.invoiceImpact.compatibleInvoices} Compatible
                  </p>
                  <p className="text-sm text-green-600">
                    Will continue to work
                  </p>
                </div>
                <div
                  className={cn(
                    'text-center p-4 rounded-lg transition-colors',
                    hasIncompatibleInvoices ? hasRemovedStyle : noRemovedStyle,
                  )}
                >
                  <XCircleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="font-semibold text-red-800">
                    {deltaAnalysis.invoiceImpact.incompatibleInvoices}{' '}
                    Incompatible
                  </p>
                  <p className="text-sm text-red-600">Will no longer process</p>
                </div>
              </div>

              {/* Incompatible Invoice Details */}
              {deltaAnalysis.invoiceImpact.incompatibleDetails.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Incompatible Invoices</h4>
                  <div className="space-y-2">
                    {deltaAnalysis.invoiceImpact.incompatibleDetails.map(
                      (invoice) => {
                        const transformedFile = invoices?.find(
                          (inv) => inv.id === invoice.invoiceId,
                        )?.files?.transformedFile;

                        return (
                          <div
                            key={invoice.invoiceId}
                            className="flex items-center justify-between p-3 bg-red-100 rounded-lg"
                          >
                            <div className="flex-1">
                              <p className="font-medium">
                                {new Date(
                                  invoice.invoiceDate,
                                ).toLocaleDateString()}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {invoice.affectedLineItems} line items affected
                                • Missing:{' '}
                                {invoice.missingProductGroups.join(', ')}
                              </p>
                            </div>
                            {transformedFile && onDownloadDocument && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 px-2 ml-2 flex-shrink-0"
                                onClick={() =>
                                  onDownloadDocument(
                                    transformedFile.id,
                                    transformedFile.name,
                                  )
                                }
                              >
                                <DownloadIcon className="h-3 w-3 mr-1" />
                                Download
                              </Button>
                            )}
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Allocation Impact */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                {hasRemovedAllocations ? (
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                )}
                <h3 className="text-lg font-semibold">
                  Payment Allocation Impact
                </h3>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={cn(
                    'text-center p-4 rounded-lg transition-colors',
                    hasPreservedAllocations
                      ? hasPreservedStyle
                      : noPreservedStyle,
                  )}
                >
                  <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">
                    {deltaAnalysis.paymentImpact.preservedAllocations} Preserved
                  </p>
                  <p className="text-sm text-green-600">
                    Allocations will remain
                  </p>
                </div>
                <div
                  className={cn(
                    'text-center p-4 rounded-lg transition-colors',
                    hasRemovedAllocations ? hasRemovedStyle : noRemovedStyle,
                  )}
                >
                  <XCircleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="font-semibold text-red-800">
                    {deltaAnalysis.paymentImpact.affectedAllocations} Removed
                  </p>
                  <p className="text-sm text-red-600">From deleted rules</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Manual Payment Impact */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3">
                {hasRemovedManualPayments ? (
                  <XCircleIcon className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircleIcon className="h-5 w-5 text-green-600" />
                )}
                <h3 className="text-lg font-semibold">
                  Manually Recorded Rebates Impact
                </h3>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className={cn(
                    'text-center p-4 rounded-lg transition-colors',
                    hasPreservedManualPayments
                      ? 'bg-green-100'
                      : 'bg-green-100 opacity-75',
                  )}
                >
                  <CheckCircleIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">
                    {Math.max(
                      0,
                      deltaAnalysis.manualAmountImpact.totalManualAmounts -
                        deltaAnalysis.manualAmountImpact.affectedManualAmounts,
                    )}{' '}
                    Preserved
                  </p>
                  <p className="text-sm text-green-600">
                    Manually recorded rebates remain
                  </p>
                </div>
                <div
                  className={cn(
                    'text-center p-4 rounded-lg transition-colors',
                    hasRemovedManualPayments
                      ? 'bg-red-100'
                      : 'bg-red-100 opacity-75',
                  )}
                >
                  <XCircleIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
                  <p className="font-semibold text-red-800">
                    {deltaAnalysis.manualAmountImpact.affectedManualAmounts}{' '}
                    Removed
                  </p>
                  <p className="text-sm text-red-600">From deleted rules</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detailed Analysis Button */}
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => setShowDeltaDialog(true)}
              className="gap-2"
            >
              <WarningIcon className="h-4 w-4" />
              View Detailed Analysis
            </Button>
          </div>
        </div>
      )}

      {/* Delta Warning Dialog */}
      {showDeltaDialog && deltaAnalysis && (
        <DeltaWarningDialog
          open={showDeltaDialog}
          onClose={() => setShowDeltaDialog(false)}
          onConfirm={() => setShowDeltaDialog(false)}
          analysis={deltaAnalysis}
          isLoading={false}
          invoices={invoices}
          onDownloadDocument={onDownloadDocument}
        />
      )}
    </div>
  );
};

export default Step3PreviewImpact;
